import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import {
  getScrollPositionForKey,
  setScrollPositionForKey,
} from "utils/scrollRestorationStorage";

const POP_RESTORE_BUDGET_MS = 1500;

function runPopRestore(
  scrollEl: HTMLElement,
  observedEl: HTMLElement | null,
  saved: number
): () => void {
  let rafId = 0;
  const cleanupFns: Array<() => void> = [];

  const apply = () => {
    const maxScroll = Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight);
    scrollEl.scrollTop = Math.min(saved, maxScroll);
  };

  const start = () => {
    apply();
    const target = observedEl ?? scrollEl;
    const ro = new ResizeObserver(() => {
      apply();
    });
    ro.observe(target);
    cleanupFns.push(() => ro.disconnect());

    const deadline = performance.now() + POP_RESTORE_BUDGET_MS;
    const loop = () => {
      apply();
      if (performance.now() < deadline) {
        rafId = requestAnimationFrame(loop);
      } else {
        cleanupFns.forEach((fn) => fn());
        cleanupFns.length = 0;
      }
    };
    rafId = requestAnimationFrame(loop);
  };

  rafId = requestAnimationFrame(start);

  return () => {
    cancelAnimationFrame(rafId);
    cleanupFns.forEach((fn) => fn());
  };
}

/**
 * Scrolls the main content wrapper to top on forward navigations (PUSH / REPLACE)
 * and restores the saved offset on POP (browser back / `navigate(-1)`).
 *
 * Uses a passive scroll listener so positions are saved before the DOM swaps on navigation.
 * POP restore retries via ResizeObserver + rAF while content height settles.
 *
 * @param scrollInnerRef Optional inner column that grows with route content (for ResizeObserver).
 */
export function useScrollRestoration(
  containerRef: RefObject<HTMLElement | null>,
  scrollInnerRef?: RefObject<HTMLElement | null>
) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const locationKeyRef = useRef(location.key);
  locationKeyRef.current = location.key;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onScroll = () => {
      setScrollPositionForKey(locationKeyRef.current, el.scrollTop);
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      setScrollPositionForKey(locationKeyRef.current, el.scrollTop);
      el.removeEventListener("scroll", onScroll);
    };
  }, [containerRef]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    const innerEl = scrollInnerRef?.current ?? null;

    let cancelPop: (() => void) | undefined;

    if (navigationType === "POP") {
      const saved = getScrollPositionForKey(location.key);
      if (saved !== undefined && el) {
        cancelPop = runPopRestore(el, innerEl, saved);
      }
    } else if (navigationType === "PUSH" || navigationType === "REPLACE") {
      el?.scrollTo({ top: 0 });
    }

    return () => {
      cancelPop?.();
    };
  }, [location.key, navigationType, containerRef, scrollInnerRef]);
}
